import React, { useEffect, useState, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CustomFormInput from 'components/input/custom-form-input/CustomFormInput'
import { BUTTONS, FORM, MODALS, PLACEHOLDERS, TABLE } from 'locales/locales'
import colors from 'utils/colors'
import { icons } from 'utils/icons'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { systemActions } from 'app/slices/system.slice'
import { useLazyGetLocalizedTextsQuery, useLazyGetSystemQuery, useUpdateLocalizedTextsMutation, useUpdateSystemSettingsMutation } from 'app/services/system.service'
import CustomButton from 'components/button/CustomButton'
import KGrid from 'components/kendo/grid/KGrid'
import { GridColumn } from '@progress/kendo-react-grid'
import { FixMeLater } from 'types'
import CustomIcon from 'components/icon/CustomIcon'
import CreateInAppBrowserModal from './CreateInAppBrowserModal'
import InAppBrowserEditModal from './EditInAppBrowserModal'
import Dropdown from 'components/kendo/dropdown/DropdownList'
import useInfoModal from 'utils/hooks/useInfoModal'
import { useTranslation } from 'react-i18next'
import Tooltip from 'components/kendo/tooltips/Tooltip'
import CustomFile from 'components/file/CustomFile'
import { NOTIFICATION_LANGUAGES } from 'utils/constants'

export interface InAppBrowser {
  _id?: string
  name: string
  url: string
  isActive: boolean
  inAppBrowserLogo: string
  isExtraTab: boolean
}


interface DataState {
  take: number
  skip: number
  filter?: {
    logic: string
    filters: Array<{ field: string; operator: string; value: string | boolean }>
  }
  sort?: Array<{ field: string; dir: string }>
}

const initialDataState: DataState = {
  take: 500,
  skip: 0,
  sort: [],
}

const InAppBrowserSettings = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const dropDownOptions = useMemo(
    () => [
      {
        title: `${t('ALL')}`,
        value: '',
      },
      {
        title: `${t('STATUS')}: ${t(TABLE.ACTIVE)}`,
        value: true,
      },
      {
        title: `${t('STATUS')}: ${t(TABLE.INACTIVE)}`,
        value: false,
      },
    ],
    [t],
  )

  const [getSystemSettings] = useLazyGetSystemQuery()
  const [updateSystem] = useUpdateSystemSettingsMutation()
  const [getLocalizedTexts] = useLazyGetLocalizedTextsQuery()
  const [updateLocalizedTexts] = useUpdateLocalizedTextsMutation()
  
  const systemSettingsSlice = useAppSelector((state) => state.system.data)

  const [inAppBrowsers, setInAppBrowsers] = useState<InAppBrowser[]>([])

  const [inAppBrowserTitles, setInAppBrowserTitles] = useState<any>()

  const [statusSelectedOption, setStatusSelectedOption] = useState<{
    title: string
    value: string | boolean
  }>({ title: t('ALL'), value: '' })

  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [dataState, setDataState] = useState<DataState>(initialDataState)
  const [isLoading, setIsLoading] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [editingBrowser, setEditingBrowser] = useState<InAppBrowser | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [language, setLanguage] = useState('tr')
  const [isTitleChanged, setIsTitleChanged] = useState(false)

  const selectedLang = useMemo(() => NOTIFICATION_LANGUAGES.find((lng) => lng.value === language), [language])

  const { openModal:deleteInfoModal, closeModal:closeDeleteInfoModal, Modal:DeleteInfoModal } = useInfoModal()

  // Filtreleme, arama ve sıralama işlemlerini computed value olarak yap
  const filteredAndSortedBrowsers = useMemo(() => {
    let result = [...inAppBrowsers]
    
    // Durum filtresi
    if (statusSelectedOption?.value !== '') {
      result = result.filter((browser: InAppBrowser) => {
        return browser.isActive === statusSelectedOption?.value
      })
    }

    // Arama filtresi
    if (searchText?.trim() !== '') {
      const searchLower = searchText?.toLowerCase()?.trim() as string
      result = result.filter((browser: InAppBrowser) => {
        return (
          browser.name.toLowerCase().includes(searchLower) ||
          browser.url.toLowerCase().includes(searchLower)
        )
      })
    }

    // Sıralama
    if (dataState.sort && dataState.sort.length > 0) {
      const { field, dir } = dataState.sort[0]
      result = [...result].sort((a: FixMeLater, b: FixMeLater) => {
        // Boolean değerler için sıralama
        if (typeof a[field] === 'boolean' && typeof b[field] === 'boolean') {
          if (dir === 'asc') {
            return a[field] ? 1 : -1
          } else {
            return a[field] ? -1 : 1
          }
        }
        // String değerler için sıralama
        if (dir === 'asc') {
          return a[field].localeCompare(b[field])
        }
        return b[field].localeCompare(a[field])
      })
    }

    return result
  }, [inAppBrowsers, statusSelectedOption, searchText, dataState.sort])

  const fetchSystemSettings = async () => {
    try {
      setIsLoading(true)
      const response = await getSystemSettings({})
      if (response?.data?.data && Object.keys(response.data.data).length !== 0) {
        const browsers =
          response.data.data.inAppBrowsers?.map((browser: InAppBrowser, index: number) => ({
            ...browser,
            _id: browser._id || `browser-${index}`,
          })) || []
        const localizedTexts = await getLocalizedTexts({key: 'inAppBrowsersTitle'})
        if (localizedTexts?.data?.data && Object.keys(localizedTexts.data.data).length !== 0) {
          setInAppBrowserTitles(localizedTexts.data.data)
        } else {
          setInAppBrowserTitles({ [language]: '' })
        }
        setInAppBrowsers(browsers)
        dispatch(
          systemActions.updateInAppBrowsers({
            inAppBrowsers: browsers,
          }),
        )
      } else {
        const browsers =
          systemSettingsSlice.inAppBrowsers?.map((browser: InAppBrowser, index: number) => ({
            ...browser,
            _id: browser._id || `browser-${index}`,
          })) || []

        setInAppBrowsers(browsers)
        const localizedTexts = await getLocalizedTexts({key: 'inAppBrowsersTitle'})
        if (localizedTexts?.data?.data && Object.keys(localizedTexts.data.data).length !== 0) {
          setInAppBrowserTitles({ [language]: localizedTexts.data.data?.[language] || '' })
        } else {
          setInAppBrowserTitles({ [language]: '' })
        }
      }
    } catch (error) {
      console.error('Error fetching system settings:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSystemSettings()
  }, [])

  const changeInAppBrowserTitle = useCallback((value: string) => {
    setIsTitleChanged(true)
    setInAppBrowserTitles((prevState: FixMeLater) => ({
      ...prevState,
      [language]: value,
    })) 
  }, [language, setIsTitleChanged, setInAppBrowserTitles])

  const saveTitleChanges = useCallback(async () => {
    try {
      const browsersTitles: FixMeLater = await updateLocalizedTexts({
        key: 'inAppBrowsersTitle',
        values: inAppBrowserTitles,
      })
      if (browsersTitles?.data?.success) {
        fetchSystemSettings()
        setIsTitleChanged(false)
      }
    } catch (error) {
      console.error(error)
    }
  }, [inAppBrowserTitles, updateLocalizedTexts])

  const cancelTitleChanges = useCallback(async() => {
    setIsTitleChanged(false)
    await fetchSystemSettings()
  }, [fetchSystemSettings])

  const onOkRemoveBrowser = useCallback(async (dataItem: InAppBrowser) => {
    try {
      const browsers = inAppBrowsers.filter(
        (item: InAppBrowser) => item._id !== dataItem._id,
      )
      setInAppBrowsers(browsers)
      const system: FixMeLater = await updateSystem({
        inAppBrowsers: browsers,
        _id: systemSettingsSlice._id,
      })
      if (system?.data?.success) {
        dispatch(
          systemActions.updateInAppBrowsers({
            inAppBrowsers: browsers,
          }),
        )
        closeDeleteInfoModal()
      } 
    } catch (error) {
      console.log(error)
    }
  }, [inAppBrowsers, updateSystem, systemSettingsSlice._id, dispatch, closeDeleteInfoModal])

  const removeInAppBrowser = useCallback(async (dataItem: InAppBrowser) => {
    deleteInfoModal({
      title: t(MODALS.DELETE_IN_APP_BROWSER_INFO_TITLE),
      description: t(MODALS.THIS_ACTION_CANNOT_BE_UNDONE),
      cancelTitle: t(BUTTONS.CANCEL) as string,
      okTitle: t(BUTTONS.DELETE) as string,
      onCancel: closeDeleteInfoModal,
      onOk: () => onOkRemoveBrowser(dataItem),
    })
  }, [t, deleteInfoModal, closeDeleteInfoModal, onOkRemoveBrowser])

  const openEditModal = useCallback((browser: InAppBrowser) => {
    setEditingBrowser({ ...browser })
    setEditModalVisible(true)
  }, [])

  const closeEditModal = useCallback(() => {
    setEditModalVisible(false)
    setEditingBrowser(null)
  }, [])

  const onDataStateChange = useCallback(
    (event: FixMeLater) => {
      const updatedState = {
        ...dataState,
        sort: event.dataState.sort,
      }
      setDataState(updatedState)
    },
    [dataState],
  )

  const onChangeLanguage = useCallback((value: { value: string; title: string }) => {
    setLanguage(value.value)
    setInAppBrowserTitles((prevState: FixMeLater) => ({
      ...prevState,
      [value.value]: prevState?.[value.value] || '',
    }))
  }, [setLanguage, setInAppBrowserTitles])

  const LogoCell = useCallback(({ dataItem }: FixMeLater) => {
    return (
      <td>
        <LogoContainer>
          <CustomFile
            mimetype="image/png"
            source={dataItem.inAppBrowserLogo || icons.defaultGroupIcon.src}
            preset="input"
            filename={dataItem.name}
            thumbnail={dataItem.inAppBrowserLogo}
          />
        </LogoContainer>
      </td>
    )
  }, [])

  const StatusCell = useCallback(({ dataItem }: FixMeLater) => {
    return (
      <td>
        <StatusText active={dataItem.isActive}>{dataItem.isActive ? (t(TABLE.ACTIVE)) : (t(TABLE.INACTIVE))}</StatusText>
      </td>
    )
  }, [t])

  const ExtraTabCell = useCallback(({ dataItem }: FixMeLater) => {
    return (
      <td>
        <StatusText active={dataItem.isExtraTab}>
          {dataItem.isExtraTab ? t(BUTTONS.YES) : t(BUTTONS.NO)}
        </StatusText>
      </td>
    )
  }, [t])

  const ActionCell = useCallback(({ dataItem}: FixMeLater) => {
    return (
      <td>
        <ActionContainer>
          <StyledCustomIcon>
            <Tooltip
              position="bottom"
              parentTitle={true}
              content={
                <CustomIcon
                  source={icons.editIcon}
                  onClick={() => openEditModal(dataItem)}
                  iconPreset="grande"
                  alt="edit"
                  title={t('EDIT')}
                />
              }
            />
          </StyledCustomIcon>
          <StyledCustomIcon>
            <Tooltip
              position="bottom"
              parentTitle={true}
              content={
                <CustomIcon
                  iconPreset="grande"
                  source={icons.deleteIcon}
                  onClick={() => removeInAppBrowser(dataItem)}
                  alt="delete"
                  title={t('DELETE')}
                />
              }
            />
          </StyledCustomIcon>
        </ActionContainer>
      </td>
    )
  }, [t, removeInAppBrowser, openEditModal])

  return (
    <ContentCard>
      <ContentCardHeader>{t(FORM.IN_APP_BORWSER_SETTINGS)}</ContentCardHeader>
      <ContentCardBody>
        <FilterContainer>
          <LeftSection>
            <TitleInputContainer>
              <CustomFormInput
                title={t(FORM.IN_APP_BORWSER_SETTINGS_TITLE)}
                placeholder={t(PLACEHOLDERS.IN_APP_BORWSER_TITLE)}
                value={inAppBrowserTitles?.[language] || ''}
                onChange={(value: string) => changeInAppBrowserTitle(value)}
                type="text"
              />
              <LanguageInputContainer>
                <CustomFormInput
                  selectedOption={
                    selectedLang ? { ...selectedLang, title: t(selectedLang.title) } : null
                  }
                  onChange={onChangeLanguage}
                  dropDownData={NOTIFICATION_LANGUAGES.map((lng) => {
                    return { ...lng, title: t(lng.title) }
                  })}
                />
              </LanguageInputContainer>
              {isTitleChanged && (
                <TitleActionButtons>
                  <Tooltip
                    content={
                      <TitleActionButton
                        onClick={saveTitleChanges}
                        $buttonType="save"
                        title={t('SAVE')}
                      >
                        <CustomIcon source={icons.successIcon} iconPreset="medium" alt="save" />
                      </TitleActionButton>
                    }
                    position="top"
                    parentTitle={true}
                  />
                  <Tooltip
                    content={
                      <TitleActionButton
                        onClick={cancelTitleChanges}
                        $buttonType="cancel"
                        title={t('CANCEL')}
                      >
                        <CustomIcon source={icons.closeIcon} iconPreset="medium" alt="cancel" />
                      </TitleActionButton>
                    }
                    position="top"
                    parentTitle={true}
                  />
                </TitleActionButtons>
              )}
            </TitleInputContainer>
          </LeftSection>
          <RightSection>
            <FilterGroup>
              <FilterLabel>{t(FORM.FILTER)}:</FilterLabel>
              <Dropdown
                style={{ width: '200px', height: '32px' }}
                options={dropDownOptions}
                id="team-filter-dropdown"
                selectedOption={statusSelectedOption}
                onChangeOption={(option) => {
                  setStatusSelectedOption(option)
                }}
              />
            </FilterGroup>
            <SearchInput
              placeholder={t(PLACEHOLDERS.SEARCH_PLACEHOLDER)}
              value={searchText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearchText(e.target.value)
              }}
            />
            <AddButton
              text={`+ ${t(BUTTONS.ADD)}`}
              textColor="white"
              onClick={() => setCreateModalVisible(true)}
            />
            <ReloadButton
              onClick={() => {
                fetchSystemSettings()
                setStatusSelectedOption({ title: t('ALL'), value: '' })
                setSearchText('')
              }}
            >
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <img
                  className="refresh"
                  height={15}
                  src={icons.refreshIcon.src}
                  srcSet={icons.refreshIcon.srcset}
                />
              )}
            </ReloadButton>
          </RightSection>
        </FilterContainer>

        <GridWrapper>
          <KGrid
            loading={isLoading}
            datasource={filteredAndSortedBrowsers || []}
            dataState={dataState}
            pageable={false}
            total={filteredAndSortedBrowsers?.length || 0}
            editMode={false}
            sortable={true}
            onDataStateChange={onDataStateChange}
            style={{ height: '100%' }}
          >
            <GridColumn
              field="inAppBrowserLogo"
              title={t(FORM.LOGO)}
              filterable={false}
              sortable={false}
              cell={(props) => <LogoCell {...props} />}
            />
            <GridColumn field="name" title={t(FORM.NAME)} filterable={false} sortable={true} />
            <GridColumn field="url" title="URL" filterable={false} sortable={false} />
            <GridColumn
              field="isActive"
              title={t(TABLE.TEAM_STATUS)}
              sortable={true}
              cell={(props) => <StatusCell {...props} />}
            />
            <GridColumn
              field="isExtraTab"
              title={t(TABLE.SHOW_IN_MENU)}
              filterable={false}
              sortable={true}
              cell={(props) => <ExtraTabCell {...props} />}
            />
            <GridColumn
              field="actions"
              title={t(TABLE.TEAM_ACTIONS)}
              filterable={false}
              sortable={false}
              cell={(props) => <ActionCell {...props} />}
            />
          </KGrid>
        </GridWrapper>
      </ContentCardBody>

      {createModalVisible && (
        <CreateInAppBrowserModal
          fetchSystemSettings={fetchSystemSettings}
          onClose={() => setCreateModalVisible(false)}
        />
      )}
      {editModalVisible && (
        <InAppBrowserEditModal
          fetchSystemSettings={fetchSystemSettings}
          inAppBrowser={editingBrowser as InAppBrowser & { _id: string }}
          onClose={closeEditModal}
        />
      )}
      {DeleteInfoModal}
    </ContentCard>
  )
}

export default InAppBrowserSettings

const ContentCard = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2), 0 0 6px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
  border: 1px solid #e3ebf6;
`

const ContentCardHeader = styled.div`
  padding: 16px 20px;
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid #e3ebf6;
`

const ContentCardBody = styled.div`
  padding: 20px;
`


const AddButton = styled(CustomButton)`
  background-color: #1e4ab7;
  border-radius: 4px;
  font-weight: 500;
  padding: 10px 20px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  
  @media (max-width: 1024px) {
    width: 100%;
  }
`

const ReloadButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  
  &:hover {
    background-color: #f5f5f5;
  }
`

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e6e6e6;
  background-color: white;
  justify-content: space-between;
  gap: 20px;
`

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
  width: 200px;
  height: 36px;
  font-size: 14px;
  box-sizing: border-box;
  
  &::placeholder {
    color: #888;
  }
  
  &:focus {
    outline: none;
    border-color: #129fdb;
    box-shadow: 0 0 0 2px rgba(18, 159, 219, 0.1);
  }
  
  @media (max-width: 1024px) {
    width: 100%;
  }
`
const FilterLabel = styled.div`
  font-size: 14px;
  color: #333;
  margin-right: 10px;
  height: 36px;
  display: flex;
  align-items: center;
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
  height: 36px;
  
  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
  }
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  width: 500px;
  gap: 25px;
  min-width: 100px;
`

const TitleInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 400px;
`

const LanguageInputContainer = styled.div`
  margin-top: 20px;
  width: 200px;
`

const TitleActionButtons = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const TitleActionButton = styled.button<{ $buttonType: 'save' | 'cancel' }>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${props => props.$buttonType === 'save' ? colors.vime : colors.red};
  border-radius: 6px;
  background-color: ${props => props.$buttonType === 'save' ? colors.vime : colors.red};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  img {
    filter: brightness(0) invert(1);
  }
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  
  @media (max-width: 1024px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`

const GridWrapper = styled.div`
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid ${colors.light80};
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 400px;

  .k-grid {
    border: none;
    border-radius: 8px;
  }

  .k-grid-header {
    background: ${colors.light40};

    th {
      border-color: ${colors.light90};
      font-weight: 600;
      color: ${colors.vimeDark};
      text-align: center;
    }
  }

  .k-grid-content {
    td {
      border-color: ${colors.light40};
      vertical-align: middle;
      padding: 8px 16px;
    }
  }

  .k-alt {
    background-color: ${colors.light40};
  }
`

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: 50px;
  min-height: 50px;
  padding: 8px;
`

const StatusText = styled.span<{ active: boolean }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  display: inline-block;
  min-width: 50px;
  color: ${(props) => (props.active ? colors.green : colors.red)};
  background-color: ${(props) =>
    props.active ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  border: 1px solid
    ${(props) => (props.active ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)')};
`

const ActionContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  padding: 0 4px;
`

const LoadingSpinner = styled.div`
  width: 15px;
  height: 15px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const StyledCustomIcon = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  cursor: pointer;
  &:hover {
      border-radius: 28px;
      background-color: #e0e0e0;
    }
  padding: 4px 6px;
`